/**
 * Main Application Component
 * Shopping list app with API integration and PostgreSQL persistence
 * Single List Mode - One list per user
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, ShoppingBag, Sparkles, LogOut } from 'lucide-react';
import { useListData } from './hooks/useListData';
import { useCatalogueInfo } from './hooks/useCatalogueInfo';
import { ListCard } from './components/ListCard';
import { DealsPanel } from './components/DealsPanel';
import { Toast } from './components/Toast';
import { AISuggestionModal } from './components/AISuggestionModal';
import { LoginPage } from './components/LoginPage';
import { AdminButton } from './components/AdminButton';
import SmartFeaturesBar from './components/SmartFeaturesBar';
import { authService } from './services/auth.service';
import type { AIGeneratedItem } from './types/ai';
import type { NettoDeal } from './services/nettoDealsService';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const {
    lists,
    loading,
    error,
    createItem,
    toggleItem,
    deleteItem,
    createList,
    deleteList,
    refresh,
  } = useListData();

  // Load catalogue info dynamically
  const { catalogueInfo, refresh: refreshCatalogue } = useCatalogueInfo();

  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await authService.verifyToken();
      setIsAuthenticated(isValid);
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, []);

  // Auto-create default list if none exists
  useEffect(() => {
    const ensureDefaultList = async () => {
      if (!loading && isAuthenticated && lists.length === 0) {
        try {
          await createList('Ma liste de courses');
          await refresh();
        } catch (err) {
          console.error('Failed to create default list:', err);
        }
      }
    };
    ensureDefaultList();
  }, [loading, isAuthenticated, lists.length]);

  // User info
  const currentUser = authService.getUser();
  const userId = currentUser?.id || 'demo-user';

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setToastMessage('👋 Déconnexion réussie');
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setToastMessage(`🎉 Bienvenue ${authService.getUser()?.name || 'Milouze'} !`);
  };

  /**
   * Get or create the main list
   */
  const getMainList = async (): Promise<number> => {
    const activeList = lists.find(l => !l.archivedAt);
    
    if (activeList) {
      return activeList.id;
    }

    // Create default list if none exists
    const newList = await createList('Ma liste de courses');
    if (!newList || !newList.id) {
      throw new Error('Failed to create default list');
    }
    await refresh();
    return newList.id;
  };

  /**
   * Handle adding an item to the list
   */
  const handleAddItem = async (
    listId: number,
    name: string,
    category: string,
    quantity: number
  ) => {
    try {
      await createItem({
        listId,
        name,
        category,
        quantity,
        checked: false,
      });
    } catch (err) {
      console.error('Failed to create item:', err);
    }
  };

  /**
   * Handle adding items from SmartFeaturesBar
   */
  const handleAddSmartItems = async (items: any[]) => {
    try {
      const targetListId = await getMainList();

      for (const item of items) {
        await createItem({
          listId: targetListId,
          name: item.name,
          category: item.category || 'Épicerie',
          quantity: item.quantity || 1,
          checked: false,
        });
      }
      
      await refresh();
      setToastMessage(`✨ ${items.length} article${items.length > 1 ? 's' : ''} ajouté${items.length > 1 ? 's' : ''} !`);
    } catch (err) {
      console.error('Failed to add smart items:', err);
      setToastMessage('❌ Erreur lors de l\'ajout des articles');
    }
  };

  /**
   * Handle adding a deal to the shopping list
   */
  const handleAddDealToList = async (deal: NettoDeal) => {
    try {
      const targetListId = await getMainList();

      await createItem({
        listId: targetListId,
        name: deal.product,
        category: deal.category,
        quantity: 1,
        checked: false,
      });
      
      await refresh();
      setToastMessage(`🎉 "${deal.product}" ajouté ! (${deal.discount})`);
    } catch (err) {
      console.error('Failed to add deal to list:', err);
      setToastMessage('❌ Erreur lors de l\'ajout du bon plan');
    }
  };

  /**
   * Handle AI-generated items
   */
  const handleAddAIItems = async (items: AIGeneratedItem[]) => {
    try {
      const targetListId = await getMainList();

      for (const item of items) {
        await createItem({
          listId: targetListId,
          name: item.name,
          category: item.category,
          quantity: item.quantity || 1,
          checked: false,
        });
      }
      
      await refresh();
      setToastMessage(`✨ ${items.length} article${items.length > 1 ? 's' : ''} ajouté${items.length > 1 ? 's' : ''} par l'IA !`);
    } catch (err) {
      console.error('Failed to add AI items:', err);
      setToastMessage('❌ Erreur lors de l\'ajout des articles');
    }
  };

  const handleToggleItem = async (id: number) => {
    try {
      await toggleItem(id);
    } catch (err) {
      console.error('Failed to toggle item:', err);
    }
  };

  const handleDeleteItem = async (id: number) => {
    try {
      await deleteItem(id);
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const handleDeleteList = async (id: number) => {
    // In single list mode, prevent deletion
    setToastMessage('⚠️ Impossible de supprimer la liste principale');
  };

  // Show loading during auth check
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#FFD100] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  const activeLists = lists.filter(list => !list.archivedAt);
  const mainList = activeLists.length > 0 ? activeLists[0] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-4 border-[#FFD100] shadow-xl">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FFD100] rounded-xl flex items-center justify-center shadow-lg">
                <ShoppingBag className="text-slate-900" size={28} strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                  Ma Liste Netto
                </h1>
                <p className="text-gray-400 text-sm flex items-center gap-2">
                  <Sparkles size={14} />
                  Bienvenue {currentUser?.name || 'Milouze'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsAIModalOpen(true)}
                className="bg-[#FFD100] hover:bg-[#ffe066] text-slate-900 font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
              >
                <Sparkles size={18} fill="currentColor" />
                <span className="hidden sm:inline">Assistant IA</span>
              </button>
              
              <button
                onClick={() => {
                  refresh();
                  refreshCatalogue();
                }}
                disabled={loading}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50"
                aria-label="Actualiser"
              >
                <RefreshCw 
                  className={`text-white ${loading ? 'animate-spin' : ''}`} 
                  size={20} 
                />
              </button>

              <button
                onClick={handleLogout}
                className="w-10 h-10 bg-white/10 hover:bg-red-500 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Déconnexion"
                title="Déconnexion"
              >
                <LogOut className="text-white" size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Smart Features Bar */}
        <div className="mb-6">
          <SmartFeaturesBar 
            userId={userId}
            currentListId={mainList?.id}
            onAddItems={handleAddSmartItems}
          />
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-red-900">Erreur</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {loading && lists.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-block w-12 h-12 border-4 border-[#FFD100] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">Chargement de votre liste...</p>
          </div>
        )}

        {/* Single List Layout */}
        {!loading && mainList && catalogueInfo && (
          <div className="grid grid-cols-1 gap-6">
            <DealsPanel 
              lists={lists} 
              onAddDealToList={handleAddDealToList}
              catalogueName={catalogueInfo.name}
              validFrom={catalogueInfo.validFrom}
              validUntil={catalogueInfo.validUntil}
            />
            
            <ListCard
              list={mainList}
              onToggleItem={handleToggleItem}
              onDeleteItem={handleDeleteItem}
              onAddItem={handleAddItem}
              onDeleteList={handleDeleteList}
            />
          </div>
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-4 py-8 mt-16 border-t border-slate-200">
        <p className="text-center text-sm text-gray-500">
          Liste Netto - Gestion de courses avec IA Groq + PostgreSQL
        </p>
      </footer>

      {/* Admin Button (Floating) */}
      <AdminButton token={authService.getToken()} onScrapeComplete={refreshCatalogue} />

      <AISuggestionModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onAddItems={handleAddAIItems}
      />

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  );
}

export default App;
